<template>
  <InfoOverlay :is-visible="presentInfo" :info="information" @close="emits('closeInfo')" />
  <section class="body">
    <template v-if="isLoading">
      <PulseLoader :loading="true" color="#3F46D6" />
    </template>
    <template v-else>
      <h1 v-if="!imgSrc">{{ label }}</h1>
      <img v-else :src="getImageUrl(imgSrc)" alt="''" />
    </template>
  </section>
  <div
    v-if="hasDetails || showOnMapEnabled"
    class="button-group"
    :class="{ 'button-group-singular': !showOnMapEnabled }"
  >
    <ButtonToggle
      v-if="showOnMapEnabled"
      :buttonText="showOnMapTitle"
      :iconLeft="iconEyeSmall"
      :size="'extra-small'"
      :active="showOnMap"
      @onClicked="emits('toggleShowOnMap')"
    />
    <AccordionText
      v-if="hasDetails"
      :active="presentDetails"
      :size="'extrasmall'"
      :accordionText="detailsTitle"
      @onClicked="emits('toggleDetails')"
    />
  </div>
</template>
<script lang="ts" setup>
import ButtonToggle from '@/design-system/atoms/buttons/ButtonToggle.vue'
import AccordionText from '@/design-system/atoms/AccordionText.vue'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { FormatOptions, formatter } from '../../formatter'
import InfoOverlay from '../InfoOverlay.vue'
import iconEyeSmall from '@/design-system/assets/icons/Icon-Eye-Small.svg'

type Props = {
  presentDetails: boolean
  hasDetails: boolean
  presentInfo: boolean
  information?: string
  color?: string
  isLoading: boolean
  showOnMapEnabled: boolean
  showOnMap: boolean
  kpi?: string | number
  imgSrc?: string
  format?: FormatOptions
}

type Emits = {
  (e: 'toggleDetails'): void
  (e: 'toggleShowOnMap'): void
  (e: 'closeInfo'): void
}

const props = defineProps<Props>()
const emits = defineEmits<Emits>()
const { t, locale } = useI18n()

const detailsTitle = computed(() => {
  return props.presentDetails
    ? t('dashboard__text__hide-details')
    : t('dashboard__text__show-details')
})

const showOnMapTitle = computed(() => {
  return props.showOnMap ? t('dashboard__text__hide-on-map') : t('dashboard__text__show-on-map')
})

const label = computed(() => {
  if (props.kpi === undefined) {
    return '-'
  }
  const options = props.format
  return options && typeof options === 'object' && typeof props.kpi === 'number'
    ? formatter(props.kpi, locale.value, options)
    : props.kpi
})
function getImageUrl(imgSrc: string) {
  return new URL(`../../../../design-system/assets/icons/${imgSrc}`, import.meta.url).href
}
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/mixins.scss';

section.body {
  height: 90px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.body h1 {
  all: unset;
  @include font-mixins.desktop-bold-h3;
  display: block;
  text-align: center;
  color: v-bind(color);
  padding-top: 24px;
  padding-bottom: 24px;
}

.information-container {
  @include font-mixins.desktop-regular-label;
  color: variables.$color-greyscale-secondary;
  display: flex;
  flex-grow: 1;
  overflow-y: auto;
  @include mixins.scrollbar-vertical;
}

.button-group {
  color: variables.$color-primary;
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  min-height: 24px;
}

.button-group-singular {
  justify-content: center;
}
</style>
